import { Mail } from 'lucide-react';

const BILLING_EMAIL = 'billing@bhcskids.com';

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export interface WalletWithdrawProps {
  withdrawableCents: number;
  nonWithdrawableCents: number;
}

export function WalletWithdraw({ withdrawableCents, nonWithdrawableCents }: WalletWithdrawProps) {
  const withdrawableFormatted = formatUsdFromCents(withdrawableCents);

  const mailtoSubject = encodeURIComponent('BHCS Cash Withdrawal Request');
  const mailtoBody = encodeURIComponent(
    `Hi BHCS Billing Team,\n\nI would like to request a withdrawal from my BHCS Cash account.\n\nWithdrawal amount: $\nPreferred return method: (Original card / Zelle / Cash)\n\nPlease let me know if you need any additional information.\n\nThank you`
  );
  const mailtoHref = `mailto:${BILLING_EMAIL}?subject=${mailtoSubject}&body=${mailtoBody}`;

  return (
    <div className="space-y-6">
      {/* Withdrawable balance */}
      <div className="text-center py-4">
        <p className="text-sm text-gray-500 mb-1">Available to withdraw</p>
        <p
          className="text-4xl font-bold text-gray-900"
          data-testid="withdraw-available-amount"
        >
          {withdrawableFormatted}
        </p>
        <p className="text-xs text-gray-500 mt-1">Withdrawable balance only</p>
      </div>

      {/* Non-withdrawable exclusion notice */}
      {nonWithdrawableCents > 0 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="non-withdrawable-notice"
        >
          <strong>{formatUsdFromCents(nonWithdrawableCents)} bonus credit</strong> is
          non-withdrawable and cannot be refunded as cash. Bonus credit can only be used to
          pay BHCS invoices.
        </div>
      )}

      {/* Withdrawal instructions */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">How to request a withdrawal</h2>
        <p className="text-sm text-gray-700">
          Email the BHCS billing team to request a withdrawal. Include the following in your
          message:
        </p>
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
          <li>The amount you wish to withdraw</li>
          <li>Your preferred return method: original card, Zelle, or cash</li>
        </ol>
        <p className="text-sm text-gray-500">
          Withdrawals are reviewed by the school admin and are typically processed within
          2 business days.
        </p>
      </div>

      {/* mailto CTA */}
      <a
        href={mailtoHref}
        className="flex items-center justify-center gap-2 w-full rounded-md bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        data-testid="withdraw-email-cta"
      >
        <Mail className="w-4 h-4" />
        Email {BILLING_EMAIL}
      </a>

      <p className="text-center text-xs text-gray-400">
        No in-app withdrawal form — withdrawals are processed by the BHCS admin team.
      </p>
    </div>
  );
}
